/**
 * Rename Folder Dialog
 * 
 * Allows renaming a folder (collection key).
 * WARNING: This also updates all references in child collections' group field.
 */

import { useState, useEffect } from 'react';
import { FolderEdit, AlertTriangle } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCollections, useRenameFolder } from '@/hooks/useCollections';
import type { Collection } from '@/api/collections';

interface RenameFolderDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    folder: Collection | null;
}

export function RenameFolderDialog({ open, onOpenChange, folder }: RenameFolderDialogProps) {
    const [newFolderKey, setNewFolderKey] = useState('');

    const { data: collections } = useCollections();
    const renameFolder = useRenameFolder();

    // Count child collections (including nested ones)
    const countChildren = (parentKey: string): number => {
        if (!collections) return 0;
        const directChildren = collections.filter(c => c.meta?.group === parentKey);
        let total = directChildren.length;
        // Recursively count nested folders' children
        directChildren.forEach(child => {
            if (!child.schema) { // If it's a folder
                total += countChildren(child.collection);
            }
        });
        return total;
    };

    const childCount = folder ? countChildren(folder.collection) : 0;

    // Initialize form when dialog opens or folder changes
    useEffect(() => {
        if (open && folder) {
            setNewFolderKey(folder.collection);
        }
    }, [open, folder]);

    const handleRename = async () => {
        if (!folder || !newFolderKey.trim()) {
            return;
        }

        if (newFolderKey === folder.collection) {
            return;
        }

        // Validate folder key format
        const validKey = /^[a-z_][a-z0-9_]*$/.test(newFolderKey);
        if (!validKey) {
            return;
        }

        // Check if name already exists
        const exists = collections?.some(c => c.collection === newFolderKey);
        if (exists) {
            return;
        }

        try {
            await renameFolder.mutateAsync({
                collection: folder.collection,
                newName: newFolderKey,
            });
            onOpenChange(false);
        } catch (error) {
            // Error is handled by the mutation's onError
        }
    };

    if (!folder) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FolderEdit className="h-5 w-5" />
                        Rename Folder
                    </DialogTitle>
                    <DialogDescription>
                        Rename "{folder.collection}" to a new folder key.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                    {/* Warning */}
                    <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            <strong>Important:</strong> Renaming a folder will update the folder key and all references in child collections ({childCount} item{childCount !== 1 ? 's' : ''}).
                            This operation cannot be undone.
                        </AlertDescription>
                    </Alert>

                    {/* Current Name */}
                    <div className="grid gap-2">
                        <Label>Current Name</Label>
                        <Input value={folder.collection} disabled />
                    </div>

                    {/* New Name */}
                    <div className="grid gap-2">
                        <Label htmlFor="new-folder-key">
                            New Folder Key <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="new-folder-key"
                            value={newFolderKey}
                            onChange={(e) => setNewFolderKey(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                            placeholder="new_folder_name"
                            autoFocus
                        />
                        <p className="text-xs text-muted-foreground">
                            Lowercase letters, numbers, and underscores only.
                        </p>
                    </div>

                    {/* Child Collections Info */}
                    {childCount > 0 && (
                        <div className="rounded-md border border-border p-3 text-sm">
                            <p className="font-medium mb-1">Affected Collections:</p>
                            <p className="text-muted-foreground">
                                {childCount} collection{childCount !== 1 ? 's' : ''} will be updated to reference the new folder name.
                            </p>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={renameFolder.isPending}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRename}
                        disabled={renameFolder.isPending || !newFolderKey.trim() || newFolderKey === folder.collection}
                    >
                        {renameFolder.isPending ? 'Renaming...' : 'Rename Folder'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
